import * as tf from '@tensorflow/tfjs-core';
import * as faceLandmarksDetection from '@tensorflow-models/face-landmarks-detection';
import * as tfjsWasm from '@tensorflow/tfjs-backend-wasm';

export const sleep = m => new Promise(r => setTimeout(r, m));

export const canvasToArrayBuffer = canvas => {
  return new Promise((resolve, reject) => canvas.toBlob(async d => {
    if (d) {
      const r = new FileReader();
      r.addEventListener('loadend', e => {
        return r.result
          ? resolve(r.result)
          : reject(new Error('Expected Fileradiuseader result'));
      });
      r.addEventListener('error', e => reject(e));
      r.readAsArrayBuffer(d);
    } else {
      reject(new Error('Expected toBlob() to be defined'));
    }
  }, 'image/jpeg', 0.8));
};

export const getImageUrl = (imgData, width, height) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.width = width;
  canvas.height = height;
  ctx.putImageData(imgData, 0, 0);
  return canvas.toDataURL();
};

export const getRectCoords = landmarks => {
  let minX = 100000,
    minY = 100000,
    maxX = 0,
    maxY = 0;
  landmarks.forEach(item => {
    minX = Math.min(minX, item[0]);
    minY = Math.min(minY, item[1]);
    maxX = Math.max(maxX, item[0]);
    maxY = Math.max(maxY, item[1]);
  });
  minX = Math.round(minX);
  maxX = Math.round(maxX);
  minY = Math.round(minY);
  maxY = Math.round(maxY);
  return [minX, minY, maxX - minX, maxY - minY];
};

export const rectCoordsForCut = (landmarks, scale, shiftY) => {
  const [x, y, width, height] = getRectCoords(landmarks);
  const widthScaled = width * scale;
  const heightScaled = height * scale;
  const newX = Math.max(0, (x + width / 2) - widthScaled / 2);
  const newY = Math.max(0, ((y + height / 2) - heightScaled / 2) - height * shiftY);
  return [newX, newY, widthScaled, heightScaled];
};

export const isMobile = () => {
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isiOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  return isAndroid || isiOS;
};

export const isChrome = () => {
  return /chrome/i.test(navigator.userAgent);
};

const rollAngle = landmarks => {
  const eyePoints = [[landmarks[33][0], landmarks[33][1]], [landmarks[263][0], landmarks[263][1]]];
  const eyeDist = eyePoints[1][1] - eyePoints[0][1];
  const eyeHight = eyePoints[1][0] - eyePoints[0][0];
  return Math.atan(eyeDist / eyeHight) * 180 / Math.PI;
};

export const getMaskAngle = landmarks => {
  const centerPoint = mean3d(landmarks);
  const newNose = normalize([landmarks[4]], centerPoint)[0];
  const dist = Math.sqrt(newNose[0] ** 2 + newNose[1] ** 2 + newNose[2] ** 2);
  const yaw = (-Math.atan(newNose[0] / newNose[2]) * 180) / Math.PI;
  const pitch = (-Math.asin(newNose[1] / dist) * 180) / Math.PI;
  return [Math.round(yaw), Math.round(pitch), Math.round(rollAngle(landmarks))];
};

export const getVector = (yaw, pitch) => {
  const z = -Math.abs(Math.cos(yaw * Math.PI / 180));
  return [
    Math.sin(yaw * Math.PI / 180),
    -Math.tan(pitch * Math.PI / 180),
    z,
  ];
};

export const getTargetPoint = (
  angle,
  centerPoint,
  radius,
  delta = 0,
  prevPoint = [0, 0, 0],
  smooth = false
) => {
  const [yaw, pitch] = angle;
  const newCenter = [0, 0, 0];
  const vectorPoint = vector(getVector(yaw, pitch), newCenter);
  const targetItem = target(vectorPoint, radius);
  const xxTarget = targetItem.map(item => vectorPoint[0] * item);
  const yyTarget = targetItem.map(item => vectorPoint[1] * item);
  const zzTarget = targetItem.map(item => vectorPoint[2] * item);
  return smooth
    ? [
      Math.round((xxTarget[1] + centerPoint[0] + prevPoint[0]) / 2),
      Math.round((yyTarget[1] + centerPoint[1] + prevPoint[1] - delta) / 2),
      Math.round((zzTarget[1] + centerPoint[2] + prevPoint[2]) / 2),
    ] : [
      Math.round((xxTarget[1] + centerPoint[0])),
      Math.round((yyTarget[1] + centerPoint[1] - delta)),
      Math.round((zzTarget[1] + centerPoint[2])),
    ];
};

export const mean3d = landmarks => {
  const { length } = landmarks;
  let xArray = 0,
    yArray = 0,
    zArray = 0;
  for (const item of landmarks) {
    xArray += item[0];
    yArray += item[1];
    zArray += item[2];
  }
  return [xArray / length, yArray / length, zArray / length];
};

const normalize = (landmarks, centerPoint) => {
  return landmarks.map(item => [
    item[0] - centerPoint[0],
    item[1] - centerPoint[1],
    item[2] - centerPoint[2],
  ]);
};

export const vector = (point1, point2) => {
  const l = point2[0] - point1[0];
  const m = point2[1] - point1[1];
  const n = point2[2] - point1[2];
  return [l, m, n];
};

export const target = (point, radius) => {
  const x = point[2];
  const y = point[0];
  const a = x ** 2 + y ** 2;
  const c = radius ** 2;
  const d = 4 * a * c;
  const t1 = Math.sqrt(d) / (2 * a);
  return [t1, -t1];
};

export const distanceCylinder = (yawTarget, yTarget, yawCurrent, yCurrent, radius) => {
  // console.log('[distanceCylinder]');
  const xCurrent = 2 * Math.PI * radius + (yawCurrent) * (Math.PI * radius / 180);
  const xTarget = 2 * Math.PI * radius + (yawTarget) * (Math.PI * radius / 180);
  return Math.sqrt((xTarget - xCurrent) ** 2 + (yTarget - yCurrent) ** 2);
};

export const initModel = async () => {
  console.warn('[initModel]');
  const BACKEND_WEBGL = true;
  const wasmUrl = './tfjs-backend-wasm.wasm';
  tfjsWasm.setWasmPath(wasmUrl);
  const initBackend = async name => {
    await tf.setBackend(name);
    return faceLandmarksDetection.load(
      faceLandmarksDetection.SupportedPackages.mediapipeFacemesh,
      { maxFaces: 1 }
    );
  };
  let model = null;
  try {
    const type = BACKEND_WEBGL ? 'webgl' : 'wasm';
    model = await initBackend(type);
    model.type = type;
  } catch {
    console.warn('[main] failed to load model with WebGL backed, trying wasm');
    if (!BACKEND_WEBGL) return null;
    try {
      model = await initBackend('wasm');
      model.type = 'wasm';
    } catch (error) {
      console.error(`[main] load model error ${error}`);
    }
  }
  const usedBackend = tf.getBackend();
  console.warn(`using backend ${usedBackend}`);
  return [model, usedBackend];
};

export const setupCamera = async video => {
  // alert(`[setupCamera] isMobile ${isMobile()}`);
  return new Promise(async resolve => {
    try {
      // const permissionObj = await navigator.permissions.query({ name: 'camera' });
      // console.log('[setupCamera]', permissionObj);

      // const available = await navigator.mediaDevices.enumerateDevices();
      // console.log({ available });
      const stream = await navigator.mediaDevices.getUserMedia({
        'audio': false,
        video: {
          facingMode: 'user',
          width: isMobile()
            ? undefined
            : { ideal: 640, max: 640 },
          height: isMobile()
            ? undefined
            : { ideal: 480, max: 1080 },
        },
      });
      console.log({ video, stream });
      video.srcObject = stream;
      video.onloadedmetadata = () => resolve(video);
    } catch (error) {
      console.error('[setupCamera]', error);
      // alert(`[setupCamera]: ${error.message}`);
      resolve(null);
    }
  });
};

export const getFacePositionCaption = value => {
  // console.log('[getFacePositionCaption]', value);
  if (value.noFace) return 'no face';
  if (value.inDistance === undefined) return 'wait';
  if (value.inDistance && value.inCenter) return 'OK';
  if (!value.inCenter) return 'not in center';
  if (value.tooFar) return 'too far';
  if (value.tooClose) return 'too close';
};

export const debouncer = ({ position, success, maxCount }) => {
  if (position.inPosition !== success) {
    // console.log(`other position [was ${position.inPosition},
    // now: ${success}], position.count: ${position.count}`);
    position.count--;
    if (position.count <= 0) {
      // console.log(`switch to ${success}`);
      position.inPosition = success;
      position.count = maxCount;
      return true;
    }
  } else if (position.count < maxCount) {
    // console.log('increase debounce');
    position.count++;
    return false;
  }
  return false;
};

const randomArbitrary = (min, max) => {
  return Math.random() * (max - min) + min;
};

const shuffleArray = unshuffled => {
  return unshuffled
    .map(a => ({ sort: Math.random(), value: a }))
    .sort((a, b) => a.sort - b.sort)
    .map(a => a.value);
};

export const generateTasks = () => {
  const tasksCount = 4;
  const maxAngle = 30;
  const minAngle = 30;
  const sectors = [
    { posX: true, posY: true },
    { posX: false, posY: true },
    { posX: false, posY: false },
    { posX: true, posY: false },
  ];
  let sectorsLeft = [];
  const tasks = shuffleArray(Array.from({ length: tasksCount }).map((item, index) => {
    const sign = points => {
      sectorsLeft.length || (sectorsLeft = shuffleArray([...sectors]));
      const nextSector = sectorsLeft.pop();
      return [
        nextSector.posX ? points[0] : -points[0],
        nextSector.posY ? points[1] : -points[1],
      ];
    };
    // const point1 = Math.round(
    //   index ? randomArbitrary(minAngle, maxAngle) : maxAngle
    // );
    // const point2 = Math.round(
    //   index ? randomArbitrary(minAngle, maxAngle) : randomArbitrary(0, maxAngle)
    // );
    const point1 = Math.round(
      index ? randomArbitrary(minAngle, maxAngle) : 45
    );
    const point2 = Math.round(
      index ? randomArbitrary(minAngle, maxAngle) : 0
    );
    return sign(
      Math.random() < 0.5
        ? [point1, point2]
        : [point2, point1]
    );
  }));
  // tasks[0] = [0, 30];
  // tasks[1] = [30, 30];
  console.log(tasks[0], tasks[1], tasks[2], tasks[3]);
  return tasks;
};

export const sendMessageToParent = message => {
  window.parent.postMessage(message, '*');
};

export const Base64 = {
  _keyStr : 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=',
  decode(input) {
    let output = '';
    let chr1,
      chr2,
      chr3;
    let enc1,
      enc2,
      enc3,
      enc4;
    let i = 0;
    input = input.replace(/[^A-Za-z0-9+/=]/g, '');
    while (i < input.length) {
      enc1 = Base64._keyStr.indexOf(input.charAt(i++));
      enc2 = Base64._keyStr.indexOf(input.charAt(i++));
      enc3 = Base64._keyStr.indexOf(input.charAt(i++));
      enc4 = Base64._keyStr.indexOf(input.charAt(i++));
      chr1 = (enc1 << 2) | (enc2 >> 4);
      chr2 = ((enc2 & 15) << 4) | (enc3 >> 2);
      chr3 = ((enc3 & 3) << 6) | enc4;
      output += String.fromCharCode(chr1);
      if (enc3 !== 64) {
        output += String.fromCharCode(chr2);
      }
      if (enc4 !== 64) {
        output += String.fromCharCode(chr3);
      }
    }
    output = Base64._utf8_decode(output);
    return output;
  },
  _utf8_decode(utftext) {
    let string = '';
    let i = 0;
    let c = 0,
      c2 = 0,
      c3;
    while (i < utftext.length) {
      c = utftext.charCodeAt(i);
      if (c < 128) {
        string += String.fromCharCode(c);
        i++;
      } else if ((c > 191) && (c < 224)) {
        c2 = utftext.charCodeAt(i + 1);
        string += String.fromCharCode(((c & 31) << 6) | (c2 & 63));
        i += 2;
      } else {
        c2 = utftext.charCodeAt(i + 1);
        c3 = utftext.charCodeAt(i + 2);
        string += String.fromCharCode(((c & 15) << 12) | ((c2 & 63) << 6) | (c3 & 63));
        i += 3;
      }
    }
    return string;
  },
};
